import React, { useState } from 'react';
import moment from 'moment';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AvailabilitySidePane from './AvailabilitySidePane';
import CalendarWeekView from '../calendar/CalendarWeekView';
import CalendarDayView from '../calendar/CalendarDayView';
import CustomDialog from '../common/CustomDialog';
import EditAvailabilityForm from './EditAvailabilityForm';
import NewAvailabilityForm from './NewAvailabilityForm';
import VacationTimeDialog from '../scheduling/VacationTimeDialog';
import useError from '../../hooks/useError';
import useIsMobile from '../../hooks/useIsMobile';
import { getEventBlocks, getAvailabilityBlocks } from '../calendar/calendarUtils';
import { useCreateVacationTimeMutation } from '../../redux/services/vacationTimeApi';

const handleSubmit = ({
  createVacationTime,
  vacationTimesQuery,
  setVacationDialogIsOpen,
  setError,
}) => formData => {
  const response = createVacationTime(formData);
  if (response.error) return setError({message: 'Error creating vacation time'});
  setError(null);
  setVacationDialogIsOpen(false);
};

const AvailabilityCalendarScreen = ({
  date,
  awayTimes = [],
  events = [],
  availabilities = [],
  calendarView = 'week',
  boxStyles = {
    backgroundColor: 'white',
    // overflowY: 'hidden',
    borderRadius: 3,
    boxShadow: 1,
    border: 1,
    borderColor: 'divider',
  },
  startHour = 8,
  endHour = 20,
  onDateChange = (e, date) => console.log('Change date', date),
  onCalendarViewChange = (e, view) => console.log('Change calendar view', view),
}) => {
  const [selectedAvailability, setSelectedAvailability] = useState();
  const [createAvailabilityDialogOpen, setCreateAvailabilityDialogOpen] = useState(false);
  const [vacationTimeDialogOpen, setVacationTimeDialogOpen] = useState(false);
  const [createVacationTime] = useCreateVacationTimeMutation();
  const error = useError();
  const isMobile = useIsMobile();
  const awayTimeBlocks = (
    events && events.length
    ? getEventBlocks({
        timeStart: moment(date).startOf(calendarView).toISOString(),
        timeEnd: moment(date).endOf(calendarView).toISOString(),
        startHour,
        endHour,
      })(events)
    : []
  );
  const availabilityBlocks = (
    availabilities && availabilities.length
    ? getAvailabilityBlocks({ date, startHour, endHour })(availabilities)
    : []
  );
  const onSubmitVacationTime = handleSubmit({
    createVacationTime,
    setVacationDialogIsOpen: setVacationTimeDialogOpen,
    setError: error.create,
  });
  return (
    <Grid container spacing={1} sx={{ml: {xs: -1}, pb: {xs: 4}}}>
      <Grid item sx={12} md={9}>
        <Stack direction='row' spacing={1} justifyContent='space-between' pt={1} pb={1}>
          <Stack direction='row' spacing={1}>
            <Button
              color='primary'
              variant='contained'
              size='small'
              onClick={() => setCreateAvailabilityDialogOpen(true)}
            >
              <AddIcon/>
              Add Zone
            </Button>
            {/* <Button
              color='info'
              variant='contained'
              size='small'
              onClick={() => setVacationTimeDialogOpen(true)}
            >
              <AddIcon/>
              Block Away Time
            </Button> */}
          </Stack>
          <ButtonGroup>
            <Button
              size='small'
              variant={calendarView === 'week' ? 'contained' : 'outlined'}
              color='primary'
              onClick={e => onCalendarViewChange(e, 'week')}
            >
              Week
            </Button>
            <Button
              size='small'
              variant={calendarView === 'day' ? 'contained' : 'outlined'}
              color='primary'
              onClick={e => onCalendarViewChange(e, 'day')}
              style={{minWidth: '60px', fontSize: '0.875rem'}}
            >
              Day
            </Button>
          </ButtonGroup>
        </Stack>
        {calendarView === 'week' &&
          <CalendarWeekView
            date={date}
            eventBlocks={awayTimeBlocks}
            availabilityBlocks={availabilityBlocks}
            boxStyles={boxStyles}
            onDateChange={onDateChange}
            onClickAvailability={(e, id) => setSelectedAvailability(id)}
          />
        }
        {
          calendarView === 'day' &&
          <CalendarDayView
            date={date}
            eventBlocks={awayTimeBlocks}
            availabilityBlocks={availabilityBlocks}
            boxStyles={boxStyles}
            onDateChange={onDateChange}
            onClickAvailability={(e, id) => setSelectedAvailability(id)}
          />
        }
      </Grid>
      <Grid item md={3} sx={{display: {xs: 'none', sm: 'block'}}}>
        <AvailabilitySidePane
          availabilities={availabilities}
          selectedAvailability={selectedAvailability}
          boxStyles={boxStyles}
          onSelectAvailability={id => setSelectedAvailability(id)}
        />
      </Grid>
      <VacationTimeDialog 
        isOpen={vacationTimeDialogOpen}
        onClose={() => setVacationTimeDialogOpen(false)}
        onSubmit={onSubmitVacationTime}
      />
      <CustomDialog
        open={createAvailabilityDialogOpen}
        onClose={() => setCreateAvailabilityDialogOpen(false)}
        fullWidth={true}
        maxWidth='xs'
        title='New Availability Zone'
      >
        <NewAvailabilityForm 
          afterSubmit={() => setCreateAvailabilityDialogOpen(false)} 
        />
      </CustomDialog>
      <CustomDialog
        open={selectedAvailability !== null && isMobile}
        onClose={() => setSelectedAvailability(null)}
        fullWidth={true}
        maxWidth='xs'
        title='Edit Availability'
      >
        {isMobile && selectedAvailability !== null &&
          <EditAvailabilityForm
            availability={availabilities.find(a => a.id === selectedAvailability)}
            afterSubmit={() => setSelectedAvailability(null)} 
          />
        }
      </CustomDialog>
    </Grid>
  );
};

export default AvailabilityCalendarScreen;